'use client'

import { QuickMessage } from '@/lib/api/messaging/quick-messages-api'
import type { LaunchableSalesFlow } from '@/lib/api/sales/flows-api'
import { useEffect, useRef } from 'react'
import { MdAccountTree, MdAttachFile, MdBolt } from 'react-icons/md'

interface Props {
    messages: QuickMessage[]
    /** Fluxos que o atendente pode iniciar para o lead desta conversa. Vêm DEPOIS das mensagens no índice. */
    flows: LaunchableSalesFlow[]
    /** Por que não há fluxos (conversa sem lead, motor desligado). Nulo = seção normal. */
    flowsUnavailableReason?: string | null
    activeIndex: number
    loading: boolean
    query: string
    onSelect: (message: QuickMessage) => void
    onSelectFlow: (flow: LaunchableSalesFlow) => void
    onHover: (index: number) => void
    onClose: () => void
}

/** Prévia curta: o popover mostra o que é, não o texto inteiro. */
function preview(body: string): string {
    const flat = body.replace(/\s+/g, ' ').trim()
    return flat.length > 80 ? `${flat.slice(0, 80)}…` : flat
}

/**
 * Lista que abre acima do campo de mensagem quando o atendente digita "/".
 *
 * A navegação por teclado fica no componente pai (junto do `onKeyDown` do
 * textarea): o foco NUNCA sai do campo de texto, senão o atendente perderia o
 * cursor no meio da digitação.
 */
const QuickMessagePicker = ({
    messages,
    flows,
    flowsUnavailableReason,
    activeIndex,
    loading,
    query,
    onSelect,
    onSelectFlow,
    onHover,
    onClose,
}: Props) => {
    const listRef = useRef<HTMLDivElement>(null)

    // Mantém o item selecionado visível quando a navegação passa do que cabe.
    useEffect(() => {
        const container = listRef.current
        const item = container?.querySelector<HTMLElement>(
            `[data-index="${activeIndex}"]`
        )
        item?.scrollIntoView({ block: 'nearest' })
    }, [activeIndex])

    return (
        <div className="absolute bottom-full left-0 right-0 mb-2 z-50">
            <div className="bg-base-100 border border-base-300 rounded-lg shadow-xl overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b border-base-300 bg-base-200/60">
                    <span className="text-xs font-medium text-base-content/70 flex items-center gap-1.5">
                        <MdBolt className="text-secondary" />
                        Mensagens rápidas e fluxos
                    </span>
                    <span className="text-[10px] text-base-content/50">
                        ↑ ↓ navegar · Enter escolher · Esc fechar
                    </span>
                </div>

                <div ref={listRef} className="max-h-72 overflow-y-auto">
                    {loading ? (
                        <div className="p-4 flex justify-center">
                            <span className="loading loading-spinner loading-sm" />
                        </div>
                    ) : (
                        <>
                            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-base-content/50">
                                Mensagens rápidas
                            </div>
                            {messages.length === 0 ? (
                                <div className="px-3 pb-2 text-sm text-base-content/60">
                                    {query
                                        ? `Nenhuma mensagem rápida para "/${query}"`
                                        : 'Nenhuma mensagem rápida disponível.'}
                                </div>
                            ) : (
                                messages.map((msg, index) => (
                                    <button
                                        key={msg.id}
                                        type="button"
                                        data-index={index}
                                        className={`w-full text-left px-3 py-2 flex items-start gap-3 transition-colors ${
                                            index === activeIndex
                                                ? 'bg-secondary/15'
                                                : 'hover:bg-base-200'
                                        }`}
                                        onMouseEnter={() => onHover(index)}
                                        // mousedown, não click: o clique tira o foco do
                                        // textarea e o blur fecharia a lista antes de
                                        // registrar a escolha.
                                        onMouseDown={e => {
                                            e.preventDefault()
                                            onSelect(msg)
                                        }}
                                    >
                                        <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-base-200 text-base-content/80 shrink-0 mt-0.5">
                                            /{msg.trigger}
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="flex items-center gap-1.5 text-sm font-medium truncate">
                                                {msg.title}
                                                {msg.mediaUrls &&
                                                    msg.mediaUrls.length > 0 && (
                                                        <MdAttachFile className="text-base-content/50 shrink-0" />
                                                    )}
                                            </span>
                                            <span className="block text-xs text-base-content/60 truncate">
                                                {preview(msg.body)}
                                            </span>
                                        </span>
                                    </button>
                                ))
                            )}

                            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-base-content/50 border-t border-base-300">
                                Fluxos
                            </div>
                            {flowsUnavailableReason ? (
                                <div className="px-3 pb-2 text-sm text-base-content/60">
                                    {flowsUnavailableReason}
                                </div>
                            ) : flows.length === 0 ? (
                                <div className="px-3 pb-2 text-sm text-base-content/60">
                                    {query
                                        ? `Nenhum fluxo para "/${query}"`
                                        : 'Nenhum fluxo liberado para o chat.'}
                                </div>
                            ) : (
                                flows.map((flow, flowIndex) => {
                                    const index = messages.length + flowIndex
                                    return (
                                        <button
                                            key={`flow-${flow.id}`}
                                            type="button"
                                            data-index={index}
                                            className={`w-full text-left px-3 py-2 flex items-start gap-3 transition-colors ${
                                                index === activeIndex
                                                    ? 'bg-secondary/15'
                                                    : 'hover:bg-base-200'
                                            }`}
                                            onMouseEnter={() => onHover(index)}
                                            onMouseDown={e => {
                                                e.preventDefault()
                                                onSelectFlow(flow)
                                            }}
                                        >
                                            <MdAccountTree className="text-secondary shrink-0 mt-0.5" />
                                            <span className="min-w-0 flex-1">
                                                <span className="block text-sm font-medium truncate">
                                                    {flow.name}
                                                </span>
                                                {flow.description && (
                                                    <span className="block text-xs text-base-content/60 truncate">
                                                        {preview(flow.description)}
                                                    </span>
                                                )}
                                            </span>
                                        </button>
                                    )
                                })
                            )}
                        </>
                    )}
                </div>

                <button
                    type="button"
                    className="w-full text-center py-1.5 text-[11px] text-base-content/50 hover:text-base-content border-t border-base-300"
                    onMouseDown={e => {
                        e.preventDefault()
                        onClose()
                    }}
                >
                    Fechar
                </button>
            </div>
        </div>
    )
}

export default QuickMessagePicker
